import { useState, useEffect, useCallback, useRef } from "react";
import { fetchMemberProducts } from "@/api/memberProducts";
import type { MemberProductsViewModel } from "@/types/memberProducts";

interface UseMemberProductsResult {
  memberProducts: MemberProductsViewModel | null;
  isLoading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
}

export function useMemberProducts(): UseMemberProductsResult {
  const [memberProducts, setMemberProducts] =
    useState<MemberProductsViewModel | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const abortRef = useRef<AbortController | null>(null);

  const load = useCallback(async () => {
    abortRef.current?.abort();
    const controller = new AbortController();
    abortRef.current = controller;

    setIsLoading(true);
    setError(null);

    const result = await fetchMemberProducts(controller.signal);

    if (controller.signal.aborted) return;

    if (result.ok) {
      setMemberProducts(result.data);
    } else {
      setError(result.error);
    }

    setIsLoading(false);
  }, []);

  useEffect(() => {
    // Defer by a microtask so React 18 StrictMode's mount → cleanup → remount
    // cycle (or any rapid effect re-run) can cancel before we hit the network.
    // Without this guard both requests reach the backend even though the first
    // one is aborted client-side.
    const timer = setTimeout(load, 0);
    return () => {
      clearTimeout(timer);
      abortRef.current?.abort();
    };
  }, [load]);

  return {
    memberProducts,
    isLoading,
    error,
    refetch: load,
  };
}
